/**
 * Реестр хозяйств. Вид по умолчанию — `farmer_default`: дехканские и резерв в
 * него не входят и не смешиваются с итогами; переключатель области видимости
 * виден, чтобы это не выглядело фильтром «по умолчанию всё».
 */

import { useCallback, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api, type ActorRow } from "../../api/client";
import { COMMON } from "../../i18n/common";
import { useRequest } from "../../api/useRequest";
import { useWindowedCollection } from "../../api/useWindowedCollection";
import { layout, PageHeader } from "../../components/AppShell";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { Select, type ChoiceOption } from "../../components/Select";
import { VirtualTable, type Column } from "../../components/VirtualTable";
import {
  Area,
  Badge,
  Button,
  Count,
  MetricCard,
  RequestErrorPanel,
  ScopeNotice,
  Text,
} from "../../components/ui";
import { identityLabel, scopeLabel } from "../../lib/epistemic";
import { levelPath } from "../../lib/levels";
import styles from "../features.module.css";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";

const PAGE_SIZE = 100;

const m = defineMessages({
  scopeFarmer: { id: 'actors.registry.scope.farmer', defaultMessage: 'Фермерские', description: 'Вариант выпадающего списка «Область видимости» в реестре хозяйств: фермерские хозяйства (вид по умолчанию)' },
  scopeFarmerHint: { id: 'actors.registry.scope.farmerHint', defaultMessage: 'вид по умолчанию', description: 'Короткая подпись справа от варианта «Фермерские» в списке области видимости' },
  scopeDehqan: { id: 'actors.registry.scope.dehqan', defaultMessage: 'Дехканские и резерв', description: 'Вариант списка «Область видимости»: дехканские хозяйства и резервный фонд' },
  scopeContext: { id: 'actors.registry.scope.context', defaultMessage: 'Прочий контекст', description: 'Вариант списка «Область видимости»: субъекты реестра, не входящие в земледелие (контекст)' },
  scopeAll: { id: 'actors.registry.scope.all', defaultMessage: 'Все субъекты', description: 'Вариант списка «Область видимости»: без отбора, все субъекты реестра' },
  allMassifs: { id: 'actors.registry.filter.allMassifs', defaultMessage: 'Все массивы', description: 'Вариант списка «Массив» в реестре хозяйств: без отбора по массиву' },
  unnamedActor: { id: 'actors.registry.unnamed', defaultMessage: 'субъект #{id}', description: 'Подпись строки реестра, когда у субъекта нет имени; {id} — номер записи' },
  colActor: { id: 'actors.registry.col.actor', defaultMessage: 'Хозяйство', description: 'Заголовок колонки таблицы реестра: имя хозяйства (ссылка на его уровень)' },
  colIdentity: { id: 'actors.registry.col.identity', defaultMessage: 'Идентичность', description: 'Заголовок колонки таблицы реестра: насколько надёжно опознано хозяйство (статус идентичности)' },
  colDirections: { id: 'actors.registry.col.directions', defaultMessage: 'Направления', description: 'Заголовок колонки таблицы реестра: направления деятельности хозяйства (коды)' },
  directionTitle: { id: 'actors.registry.direction.title', defaultMessage: '{code} (метка источника: «{label}»)', description: 'Подсказка по наведению на код направления; {code} — код направления, {label} — исходная метка из файла-источника' },
  colContours: { id: 'actors.registry.col.contours', defaultMessage: 'Контуров', description: 'Заголовок числовой колонки таблицы реестра: число контуров хозяйства' },
  colArable: { id: 'actors.registry.col.arable', defaultMessage: 'Пашня, га', description: 'Заголовок числовой колонки таблицы реестра: площадь пашни в гектарах' },
  colTotal: { id: 'actors.registry.col.total', defaultMessage: 'Всего, га', description: 'Заголовок числовой колонки таблицы реестра: вся земля хозяйства в гектарах' },
  title: { id: 'actors.registry.title', defaultMessage: 'Реестр хозяйств', description: 'Заголовок экрана и последнее звено цепочки уровней (хоким, менеджер, инспектор)' },
  purpose: { id: 'actors.registry.purpose', defaultMessage: 'Субъекты канонического реестра. Идентичность в этом срезе — кластер нормализованного имени, а не юридическое лицо: совпадение названий не доказывает, что это одно хозяйство.', description: 'Пояснение под заголовком экрана «Реестр хозяйств»: что такое субъект реестра и почему одинаковые названия не равны одному хозяйству' },
  scopeLabel: { id: 'actors.registry.filter.scope', defaultMessage: 'Область видимости', description: 'Подпись выпадающего списка: какие субъекты показывать (фермерские, дехканские…)' },
  scopeExplain: { id: 'actors.registry.filter.scopeExplain', defaultMessage: 'Вид по умолчанию — фермерские хозяйства: дехканские и резерв в итоги района не входят и с ними не смешиваются. Переключатель виден именно поэтому — чтобы «по умолчанию» не читалось как «все».', description: 'Подсказка (значок «?») у списка «Область видимости»' },
  massifLabel: { id: 'actors.registry.filter.massif', defaultMessage: 'Массив', description: 'Подпись выпадающего списка: отбор хозяйств по массиву' },
  massifExplain: { id: 'actors.registry.filter.massifExplain', defaultMessage: 'Отбор по ЗЕМЛЕ: остаются те, у кого на этой территории есть действующее землепользование, — включая тех, кому здесь ничего не поручали. Это фильтр вида, а не переход на уровень массива: список остаётся районным.', description: 'Подсказка (значок «?») у списка «Массив»: фильтр по земле, а не переход на другой уровень' },
  searchLabel: { id: 'actors.registry.filter.searchLabel', defaultMessage: 'Поиск по имени', description: 'Подпись поля поиска хозяйства по имени' },
  searchPlaceholder: { id: 'actors.registry.filter.searchPlaceholder', defaultMessage: 'часть названия', description: 'Подсказка внутри поля поиска хозяйства: вводится часть названия' },
  resetView: { id: 'actors.registry.filter.reset', defaultMessage: 'Вернуть вид по умолчанию', description: 'Кнопка в панели фильтров: сбросить область видимости, поиск и массив' },
  kpiSubjects: { id: 'actors.registry.kpi.subjects', defaultMessage: 'Субъектов в выборке', description: 'Подпись KPI (первая плитка): число субъектов реестра после фильтров' },
  allScopes: { id: 'actors.registry.kpi.allScopes', defaultMessage: 'все области видимости', description: 'Мелкая подпись под KPI: выбрана область «Все субъекты»' },
  subjectsBottomMassif: { id: 'actors.registry.kpi.bottomMassif', defaultMessage: '{scope} · массив <link>{massif}</link>', description: 'Мелкая подпись под KPI «Субъектов в выборке»; {scope} — название области видимости, {massif} — имя массива (ссылка на его уровень)' },
  kpiSubjectsExplain: { id: 'actors.registry.kpi.subjectsExplain', defaultMessage: 'Знаменатель — субъекты канонического реестра, суженные применёнными фильтрами. Считает сервер по всей выборке, а не по строкам, которые успела подгрузить таблица.', description: 'Подсказка (значок «?») у KPI «Субъектов в выборке»' },
  kpiEvery: { id: 'actors.registry.kpi.every', defaultMessage: 'Тот же отбор без области видимости', description: 'Подпись KPI (вторая плитка): сколько субъектов в том же отборе без ограничения области видимости' },
  everyOk: { id: 'actors.registry.kpi.everyOk', defaultMessage: 'дехканские, резерв и прочий контекст — тоже субъекты реестра', description: 'Мелкая подпись под KPI «Тот же отбор без области видимости», когда число получено' },
  everyLoading: { id: 'actors.registry.kpi.everyLoading', defaultMessage: 'считается', description: 'Мелкая подпись под KPI, пока число считается' },
  everyFailed: { id: 'actors.registry.kpi.everyFailed', defaultMessage: 'число не загрузилось', description: 'Мелкая подпись под KPI, когда запрос не удался' },
  kpiEveryExplain: { id: 'actors.registry.kpi.everyExplain', defaultMessage: 'Разность с числом слева — это ровно то, что прячет вид по умолчанию. Он не «все»: фермерские хозяйства отобраны намеренно, и знать цену этого отбора обязан тот, кто по нему решает.', description: 'Подсказка (значок «?») у KPI «Тот же отбор без области видимости»' },
  kpiLand: { id: 'actors.registry.kpi.land', defaultMessage: 'Земля и работы выборки', description: 'Подпись KPI (третья плитка): площадь земли и работы по выборке; числа нет' },
  kpiLandBottom: { id: 'actors.registry.kpi.landBottom', defaultMessage: 'сервер не отдаёт агрегат по выборке; гектары и контуры есть в каждой строке, обязательства — на карточке хозяйства', description: 'Мелкая подпись под KPI «Земля и работы выборки»: почему числа нет и где его искать' },
  kpiLandExplain: { id: 'actors.registry.kpi.landExplain', defaultMessage: 'Ни двух осей, ни гектаров по выборке сервер не считает. Сложить их из загруженных строк нельзя: это была бы часть, выданная за целое.', description: 'Подсказка (значок «?») у KPI «Земля и работы выборки»' },
  loadFailed: { id: 'actors.registry.loadFailed', defaultMessage: 'Реестр хозяйств не загрузился', description: 'Заголовок панели ошибки загрузки реестра хозяйств' },
  caption: { id: 'actors.registry.caption', defaultMessage: 'Реестр хозяйств района', description: 'Подпись таблицы для скринридера (aria): реестр хозяйств района' },
  foundPending: { id: 'actors.registry.found.pending', defaultMessage: 'Найдено …', description: 'Строка над таблицей реестра, пока число найденных субъектов неизвестно' },
  found: { id: 'actors.registry.found', defaultMessage: '{count, plural, one {Найден <b>#</b> субъект} few {Найдено <b>#</b> субъекта} many {Найдено <b>#</b> субъектов} other {Найдено <b>#</b> субъекта}}', description: 'Строка над таблицей реестра: сколько субъектов найдено по фильтрам' },
  updating: { id: 'actors.registry.updating', defaultMessage: ' · обновляем', description: 'Добавка к строке «Найдено…», пока таблица подгружает данные; начинается с разделителя' },
  denominator: { id: 'actors.registry.denominator', defaultMessage: 'Знаменатель: {scope}', description: 'Строка над таблицей: что взято за знаменатель; {scope} — название области видимости' },
  denominatorMassif: { id: 'actors.registry.denominatorMassif', defaultMessage: 'Знаменатель: {scope} · массив {massif}', description: 'Строка над таблицей, когда выбран массив; {scope} — область видимости, {massif} — имя массива' },
  allSubjects: { id: 'actors.registry.allSubjects', defaultMessage: 'все субъекты реестра', description: 'Значение знаменателя над таблицей: вся область видимости без отбора' },
  orderNote: { id: 'actors.registry.orderNote', defaultMessage: 'Порядок — реестровый, по идентификатору записи. Это не ранжирование: сводной оценки хозяйств в корпусе нет.', description: 'Пометка над таблицей реестра: порядок строк не рейтинг' },
  footNote: { id: 'actors.registry.footNote', defaultMessage: 'Гектары считаются на собственном зерне хозяйства: пользователь части контура получает площадь части, а не всего контура.', description: 'Примечание под таблицей реестра: чья площадь у хозяйства, пользующегося частью контура' },
});

export function ActorRegistry() {
  const t = useT();
  const scopes: ReadonlyArray<ChoiceOption> = [
    { value: "farmer_default", label: t(m.scopeFarmer), hint: t(m.scopeFarmerHint) },
    { value: "dehqan_reserve", label: t(m.scopeDehqan) },
    { value: "context_other", label: t(m.scopeContext) },
    { value: "all", label: t(m.scopeAll) },
  ];
  const context = useAnalysisContext();
  const [search, setSearch] = useSearchParams();
  const location = useLocation();

  const scope = search.get("view_scope") ?? "farmer_default";
  const query = search.get("q") ?? "";
  // Массив здесь — ФИЛЬТР ВИДА, а не ступень уровня: реестр остаётся районным
  // списком, и в цепочку уровней массив не встаёт. Иначе адрес обещал бы
  // уровень территории, а показывал бы список хозяйств района.
  const massifId = search.get("massif_id");

  const filters = useMemo(
    () => ({
      ...context.params,
      view_scope: scope,
      ...(query ? { search: query } : {}),
      ...(massifId ? { massif_id: Number(massifId) } : {}),
    }),
    [context.params, scope, query, massifId],
  );

  const fetchPage = useCallback(
    (offset: number, limit: number) => api.actors({ ...filters, limit, offset }),
    [filters],
  );

  const resetKey = `${context.key}|${scope}|${query}|${massifId ?? ""}`;
  const collection = useWindowedCollection<ActorRow>(
    fetchPage,
    resetKey,
    PAGE_SIZE,
  );

  // Сколько субъектов отсекает вид по умолчанию. Считает сервер тем же отбором
  // без области видимости: разность между этими двумя числами — единственный
  // честный способ сказать, что «по умолчанию» не значит «все».
  const everyScope = useRequest(
    () => api.actors({ ...filters, view_scope: "all", limit: 1 }),
    [resetKey],
  );
  const massifs = useRequest(() => api.massifs(context.params), [context.key]);
  const massifOptions = useMemo<ReadonlyArray<ChoiceOption>>(
    () => [
      { value: "", label: t(m.allMassifs) },
      ...(massifs.data?.items ?? []).map((massif) => ({
        value: String(massif.id),
        label: massif.canonical_name,
      })),
    ],
    [massifs.data, t],
  );
  const selectedMassif =
    massifId === null
      ? null
      : ((massifs.data?.items ?? []).find(
          (massif) => String(massif.id) === massifId,
        ) ?? null);

  const patch = (key: string, value: string | null) => {
    setSearch(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (!value) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  const columns: ReadonlyArray<Column<ActorRow>> = useMemo(
    () => [
      {
        key: "name",
        header: t(m.colActor),
        width: "minmax(220px, 2.4fr)",
        render: (row) => (
          <Link
            to={{
              pathname: levelPath("actor", row.id),
              search: location.search,
            }}
          >
            <Text value={row.name} fallback={t(m.unnamedActor, { id: row.id })} />
          </Link>
        ),
      },
      {
        key: "identity",
        header: t(m.colIdentity),
        width: "minmax(160px, 1.4fr)",
        render: (row) => {
          const identity = identityLabel(row.identity_status);
          return (
            <Badge tone={identity.known ? "neutral" : "unknown"} title={identity.label}>
              {identity.label}
            </Badge>
          );
        },
      },
      {
        key: "directions",
        header: t(m.colDirections),
        width: "minmax(200px, 2fr)",
        render: (row) =>
          row.directions.length === 0 ? (
            <span>—</span>
          ) : (
            <span
              title={row.directions
                .map((d) => t(m.directionTitle, { code: d.direction_code, label: d.raw_label }))
                .join("; ")}
            >
              {row.directions.map((d) => d.direction_code).join(", ")}
            </span>
          ),
      },
      {
        key: "contours",
        header: t(m.colContours),
        width: "minmax(90px, 0.7fr)",
        align: "right",
        render: (row) => <Count value={row.contours} />,
      },
      {
        key: "arable",
        header: t(m.colArable),
        width: "minmax(110px, 0.9fr)",
        align: "right",
        render: (row) => <Area microha={row.arable_microha} unit={null} />,
      },
      {
        key: "total",
        header: t(m.colTotal),
        width: "minmax(110px, 0.9fr)",
        align: "right",
        render: (row) => <Area microha={row.total_microha} unit={null} />,
      },
    ],
    [location.search, t],
  );

  const scopeText = scopeLabel(scope === "all" ? null : scope);

  return (
    <div className={styles.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={t(m.purpose)}
      />

      <ScopeNotice scope={collection.scope} />

      <div className={layout.filterBar} role="search">
        <Select
          label={t(m.scopeLabel)}
          value={scope}
          options={scopes}
          onChange={(value) => patch("view_scope", value)}
          explain={t(m.scopeExplain)}
        />

        <Select
          label={t(m.massifLabel)}
          value={massifId ?? ""}
          options={massifOptions}
          onChange={(value) => patch("massif_id", value || null)}
          explain={t(m.massifExplain)}
        />

        <div className={layout.field}>
          <label className={layout.fieldLabel} htmlFor="filter-actor-search">
            {t(m.searchLabel)}
          </label>
          <input
            id="filter-actor-search"
            className={layout.control}
            type="search"
            value={query}
            placeholder={t(m.searchPlaceholder)}
            onChange={(event) => patch("q", event.target.value || null)}
          />
        </div>

        {scope !== "farmer_default" || query || massifId ? (
          <Button
            onClick={() => {
              setSearch(
                (previous) => {
                  const next = new URLSearchParams(previous);
                  next.delete("view_scope");
                  next.delete("q");
                  next.delete("massif_id");
                  return next;
                },
                { replace: true },
              );
            }}
          >
            {t(m.resetView)}
          </Button>
        ) : null}
      </div>

      {/* --- Первый взгляд: сводка выборки ----------------------------- */}
      {/*
        Три числа. Третье — «нет данных» с названной причиной: агрегата земли и
        работ по выборке сервер не отдаёт, а сложить его из подгруженного окна
        нельзя — таблица виртуализирована, и такая сумма была бы частью,
        выданной за целое. Оценки хозяйств здесь нет и не появится.
      */}
      <div className={`${layout.grid} ${layout.grid3}`}>
        <MetricCard
          label={t(m.kpiSubjects)}
          value={<Count value={collection.total} />}
          bottom={
            selectedMassif ? (
              t(m.subjectsBottomMassif, {
                scope: scope === "all" ? t(m.allScopes) : scopeText.label,
                massif: selectedMassif.canonical_name,
                link: (chunks) => (
                  <Link
                    to={{
                      pathname: levelPath("massif", selectedMassif.id),
                      search: context.linkSearch(),
                    }}
                  >
                    {chunks}
                  </Link>
                ),
              })
            ) : scope === "all" ? (
              t(m.allScopes)
            ) : (
              scopeText.label
            )
          }
          explain={t(m.kpiSubjectsExplain)}
        />
        <MetricCard
          label={t(m.kpiEvery)}
          value={<Count value={everyScope.data?.page.total ?? null} />}
          bottom={
            everyScope.data
              ? t(m.everyOk)
              : everyScope.state === "loading"
                ? t(m.everyLoading)
                : t(m.everyFailed)
          }
          explain={t(m.kpiEveryExplain)}
        />
        <MetricCard
          label={t(m.kpiLand)}
          value={<Count value={null} />}
          bottom={t(m.kpiLandBottom)}
          explain={t(m.kpiLandExplain)}
        />
      </div>

      {collection.error && collection.total === null ? (
        <RequestErrorPanel
          error={collection.error}
          title={t(m.loadFailed)}
          action={<Button onClick={collection.reload}>{t(COMMON.retry)}</Button>}
        />
      ) : (
        <VirtualTable
          columns={columns}
          total={collection.total}
          rows={collection.rows}
          onRangeChange={collection.ensureRange}
          rowKey={(row) => row.id}
          caption={t(m.caption)}
          meta={
            <>
              <span>
                {collection.total === null
                  ? t(m.foundPending)
                  : t(m.found, { count: collection.total, b: (chunks) => <strong>{chunks}</strong> })}
                {collection.loading ? t(m.updating) : ""}
              </span>
              <span>
                {selectedMassif
                  ? t(m.denominatorMassif, {
                      scope: scope === "all" ? t(m.allSubjects) : scopeText.label,
                      massif: selectedMassif.canonical_name,
                    })
                  : t(m.denominator, {
                      scope: scope === "all" ? t(m.allSubjects) : scopeText.label,
                    })}
              </span>
              <span>{t(m.orderNote)}</span>
            </>
          }
          footNote={t(m.footNote)}
        />
      )}
    </div>
  );
}
